import React, { useState, useEffect, useCallback, memo } from "react";
import OptimizedImage from "./OptimizedImage";
import { Helmet } from "react-helmet-async";

const slides = [
  {
    title: "AI-Powered Digital Transformation",
    description: "Transform your business with cutting-edge AI solutions that drive innovation and growth",
    image: "/images/Advisory.png",
    alt: "AI Digital Transformation Solutions",
    category: "AI & Machine Learning",
    cta: "Explore AI Solutions"
  },
  {
    title: "Cloud-Native Microservices Architecture",
    description: "Build scalable, resilient applications with modern cloud technologies and microservices",
    image: "/images/Microservice.png",
    alt: "Cloud Microservices Architecture",
    category: "Cloud & Infrastructure",
    cta: "View Cloud Solutions"
  },
   {
    title: "Mobile & Web Application Development",
    description: "Create exceptional user experiences with our full-stack development expertise",
    image: "/images/Appdevelop2.png",
    alt: "Mobile and Web App Development",
    category: "Application Development",
    cta: "See Our Work"
  },
];

const Home = memo(() => {
  const [current, setCurrent] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const length = slides.length;

  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const timer = setInterval(() => {
      setCurrent((prev) => (prev === length - 1 ? 0 : prev + 1));
    }, 5000);
    return () => clearInterval(timer);
  }, [length, isAutoPlaying]);

  const nextSlide = useCallback(() => {
    setCurrent(current === length - 1 ? 0 : current + 1);
    setIsAutoPlaying(false);
  }, [current, length]);

  const prevSlide = useCallback(() => {
    setCurrent(current === 0 ? length - 1 : current - 1);
    setIsAutoPlaying(false);
  }, [current, length]);

  const goToSlide = useCallback((index) => {
    setCurrent(index);
    setIsAutoPlaying(false);
  }, []);

  const handleMouseEnter = useCallback(() => {
    setIsAutoPlaying(false);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsAutoPlaying(true);
  }, []);

  return (
    <>
      <Helmet>
        <title>UniwayStart - AI-Powered Software Development Solutions</title>
        <meta name="description" content="Transform your business with cutting-edge AI solutions, cloud-native microservices, and mobile app development. Expert software development services." />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="UniwayStart - AI-Powered Software Development" />
        <meta property="og:description" content="Transform your business with cutting-edge AI solutions and modern software development." />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="/images/Uniway_Logo.png" />
      </Helmet>
      
      <section 
        className="relative w-full max-w-8xl mx-auto mt-0 overflow-hidden" 
        aria-label="Featured Services Carousel"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className="relative h-[600px]">
          {slides.map((slide, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-all duration-1000 ease-in-out ${
                index === current ? "opacity-100 z-10" : "opacity-0 z-0"
              }`}
            >
              <OptimizedImage
                src={slide.image}
                alt={slide.alt}
                className="w-full h-full object-cover"
                loading={index === 0 ? "eager" : "lazy"}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 text-white p-8">
                <div className="max-w-4xl">
                  <span className="inline-block bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-medium mb-4 animate-pulse">
                    {slide.category}
                  </span>
                  <h2 className="text-4xl md:text-5xl font-bold mb-4 leading-tight transform transition-all duration-700 hover:scale-105">
                    {slide.title}
                  </h2>
                  <p className="text-xl mb-6 text-gray-200 max-w-2xl leading-relaxed">
                    {slide.description}
                  </p>
                  <button className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">
                    {slide.cta}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      
      {/* Navigation Buttons */}
      <button
        onClick={prevSlide}
        className="absolute top-1/2 left-4 transform -translate-y-1/2 bg-white/90 hover:bg-white text-black px-4 py-2 rounded-full shadow-lg transition-all duration-300 hover:scale-110"
        aria-label="Previous slide"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      </button>
      <button
        onClick={nextSlide}
        className="absolute top-1/2 right-4 transform -translate-y-1/2 bg-white/90 hover:bg-white text-black px-4 py-2 rounded-full shadow-lg transition-all duration-300 hover:scale-110"
        aria-label="Next slide"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>

        {/* Slide Indicators */}
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 hover:scale-125 ${
                index === current ? 'bg-white shadow-lg' : 'bg-white/50 hover:bg-white/75'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </section>
    </>
  );
});

Home.displayName = 'Home';

export default Home;